import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Switch } from "ctech-react-components";

import Input from "components/Input";

import { CoverageAreaProps } from "store/modules/Logistics/types.store";
import { useAppDispatch } from "store/hooks";
import {
  handleUpdateWorkingAreaRequest,
  handleDeleteWorkingAreaRequest,
  handleUpdateWorkingAreaStatusRequest,
} from "store/modules/Logistics/thunk.store";

import { useAuth } from "context/AuthContext";

import * as Styled from "./styles";

interface NameStatusData {
  name: string;
  isActive: boolean;
}

interface NameStatusCardProps {
  workingAreaData: CoverageAreaProps;
  goBack: () => void;
}

const NameStatusCard = ({ workingAreaData, goBack }: NameStatusCardProps) => {
  const [editMode, setEditMode] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { dealerId } = useAuth();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<NameStatusData>({
    defaultValues: {
      isActive: !!workingAreaData.enabled,
    },
  });

  const handleChangeEditMode = () => {
    setEditMode((state) => !state);
  };
  // eslint-disable-next-line
  const handleRemoveWorkingArea = async () => {
    await dispatch(
      handleDeleteWorkingAreaRequest({
        id: workingAreaData.id,
        dealerId,
      }),
    ).unwrap();
    goBack();
  };

  const handleEditWorkingArea = async (data: NameStatusData) => {
    await dispatch(
      handleUpdateWorkingAreaRequest({
        ...workingAreaData,
        ...data,
        dealerId,
      }),
    ).unwrap();

    handleChangeEditMode();
  };

  useEffect(() => {
    reset();
    setValue("isActive", !!workingAreaData?.enabled);
    setEditMode(false);
  }, [workingAreaData?.id, setValue, workingAreaData?.enabled, reset]);

  useEffect(() => {
    reset();
  }, [editMode, reset]);

  return (
    <Styled.Container onSubmit={handleSubmit(handleEditWorkingArea)}>
      <Styled.TopContainer>
        <Typography variant="caption">{t("nameAndStatus")}</Typography>

        <Styled.SwitchContainer editMode isActive={!!watch().isActive}>
          <Styled.IsActiveText isActive={!!watch().isActive}>
            {watch().isActive ? t("active") : t("inactive")}
          </Styled.IsActiveText>

          <Switch
            name="isActive"
            control={control}
            setValue={() => null}
            size="small"
            color="success"
            onChange={async (_, enabled) => {
              await dispatch(
                handleUpdateWorkingAreaStatusRequest({
                  enabled,
                  id: workingAreaData.id,
                  dealerId,
                }),
              ).unwrap();
              setValue("isActive", enabled);
            }}
            defaultChecked={!!workingAreaData.enabled}
          />
        </Styled.SwitchContainer>
      </Styled.TopContainer>
      {editMode ? (
        <Input
          name="name"
          error={errors.name}
          label={t("areaName")}
          defaultValue={workingAreaData.name}
          register={register}
          fullWidth
          inputProps={{ maxLength: 40 }}
          registerOptions={{
            required: t("requiredField"),
            minLength: { message: t("threeCharacteresMin"), value: 3 },
          }}
        />
      ) : (
        <Typography>{workingAreaData?.name}</Typography>
      )}

      <Styled.ActionsContainer editMode={editMode}>
        {editMode ? (
          <>
            <div> </div>
            {/* <Styled.ActionContainer
              endIcon={<DeleteForeverIcon />}
              onClick={handleRemoveWorkingArea}
            >
              {t("delete")}
            </Styled.ActionContainer> */}
            <Styled.AlignEndContainer>
              <Styled.ActionContainer
                endIcon={<CloseIcon />}
                color="inherit"
                onClick={handleChangeEditMode}
              >
                {t("close")}
              </Styled.ActionContainer>
              <Styled.ActionContainer
                endIcon={<SaveIcon />}
                color="secondary"
                type="submit"
              >
                {t("save")}
              </Styled.ActionContainer>
            </Styled.AlignEndContainer>
          </>
        ) : (
          <Styled.ActionContainer
            color="secondary"
            onClick={handleChangeEditMode}
            endIcon={<EditIcon />}
          >
            {t("edit")}
          </Styled.ActionContainer>
        )}
      </Styled.ActionsContainer>
    </Styled.Container>
  );
};

export default NameStatusCard;
